import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { AiImageEnhancementService, EnhancementType } from 'src/app/service/ai-image-enhancement.service';
import { extractBackendMessage } from 'src/app/service/admin-error';
import { MessageService } from 'src/app/service/message.service';
import { ownsAnotherPhoto, PHOTO_GONE_MESSAGE, PHOTO_MOVED_MESSAGE } from 'src/app/util/photo-upload';

/** 되돌아온 보정 결과가 아직 같은 사진 자리인지 확인할 수 있게, 교체 대상이던 URL을 함께 올린다. */
export interface PhotoReplacement {
  newUrl: string;
  previousUrl: string;
}

@Component({
  selector: 'ai-image-edit-overlay',
  templateUrl: './ai-image-edit-overlay.component.html',
  styleUrls: ['./ai-image-edit-overlay.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
      transition(':leave', [animate('200ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AiImageEditOverlayComponent implements OnChanges, OnDestroy {
  @Input() imageUrl: string;
  @Input() imageKey: string;
  @Input() disabled = false;
  @Input() showEditButton = true; // 편집 버튼 표시 여부 (admin 페이지에서만 true)

  @Output() imageUpdated = new EventEmitter<PhotoReplacement>();

  // UI State
  showOverlay = false;
  isEnhancing = false;
  error: string | null = null;
  customPrompt = '';
  showPromptInput = false;
  showLightbox = false;

  // History for undo
  private imageHistory: string[] = [];
  private historyIndex = -1;
  /** 맡은 사진이 바뀌거나 오버레이가 사라질 때 올린다. 그 전에 보낸 보정 요청의 결과는 버린다. */
  private photoGeneration = 0;
  private destroyed = false;

  // Lightbox DOM element (appended to body)
  private lightboxElement: HTMLElement | null = null;
  private lightboxListeners: (() => void)[] = [];

  constructor(
    private aiEnhancementService: AiImageEnhancementService,
    private renderer: Renderer2,
    private messageService: MessageService,
  ) {}

  /**
   * 미리보기는 사진 순번으로 뷰를 재사용하므로, 순서가 바뀌면 이 오버레이가 맡는 사진만 조용히 교체된다.
   * 옛 사진의 되돌리기 기록을 남겨 두면 되돌리기가 새 사진에 옛 URL을 써 버린다.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['imageUrl'];
    if (!change || change.isFirstChange()) return;
    if (!ownsAnotherPhoto(change.previousValue, this.imageHistory, change.currentValue)) return;
    this.photoGeneration++;
    this.imageHistory = [];
    this.historyIndex = -1;
    this.error = null;
    this.showOverlay = false;
  }

  ngOnDestroy(): void {
    this.photoGeneration++;
    this.destroyed = true;
    this.imageHistory = [];
    // 라이트박스가 열린 상태에서 컴포넌트가 파괴되면 body overflow 복원
    if (this.showLightbox) {
      document.body.style.overflow = '';
    }
    this.removeLightbox();
  }

  /**
   * 편집 오버레이 토글
   */
  toggleOverlay(): void {
    this.showOverlay = !this.showOverlay;
    if (!this.showOverlay) {
      this.showPromptInput = false;
      this.customPrompt = '';
      this.error = null;
    }
  }

  /**
   * 프롬프트 입력 토글
   */
  togglePromptInput(): void {
    this.showPromptInput = !this.showPromptInput;
    if (!this.showPromptInput) {
      this.customPrompt = '';
    }
  }

  /**
   * 이미지 보정 실행
   */
  async enhance(type: EnhancementType): Promise<void> {
    if (this.isEnhancing || !this.imageUrl) return;

    this.isEnhancing = true;
    this.error = null;
    this.showPromptInput = false;

    // 편집 전 원본 이미지 저장 (첫 번째 편집 시에만)
    const originalUrl = this.imageUrl;
    const generation = this.photoGeneration;

    try {
      // AI 편집 서비스 호출
      const newUrl = await this.aiEnhancementService.enhanceAndUpload(
        this.imageUrl,
        type,
        type === 'custom' ? this.customPrompt : undefined,
      );

      // 기다리는 사이 이 오버레이가 다른 사진을 맡았거나 사라졌으면 결과를 버린다.
      if (generation !== this.photoGeneration) {
        this.messageService.toast(this.destroyed ? PHOTO_GONE_MESSAGE : PHOTO_MOVED_MESSAGE);
        return;
      }

      // 히스토리 관리: 첫 편집이면 원본도 저장
      if (this.imageHistory.length === 0) {
        this.imageHistory.push(originalUrl);
      }
      // 새 이미지 추가
      this.imageHistory.push(newUrl);
      this.historyIndex = this.imageHistory.length - 1;

      // 이벤트 발생
      this.imageUpdated.emit({ newUrl, previousUrl: originalUrl });
      this.imageUrl = newUrl;

      // 성공 시 오버레이 닫기
      this.customPrompt = '';
      this.showOverlay = false;
    } catch (err: any) {
      console.error('AI image enhancement failed:', err);
      // 백엔드(HttpException)가 내려준 메시지(예: 429 쿼터 소진 안내)를 우선 노출한다.
      const message = extractBackendMessage(err, '이미지 보정에 실패했습니다.');
      // 이미 다른 사진을 맡았으면 그 사진 위가 아니라 토스트로 알린다.
      if (generation !== this.photoGeneration) this.messageService.toast(message);
      else this.error = message;
    } finally {
      this.isEnhancing = false;
    }
  }

  /**
   * 되돌리기
   */
  undo(): void {
    if (!this.canUndo) return;

    const replacedUrl = this.imageUrl;
    this.historyIndex--;
    const previousUrl = this.imageHistory[this.historyIndex];
    this.imageUrl = previousUrl;
    this.imageUpdated.emit({ newUrl: previousUrl, previousUrl: replacedUrl });
  }

  /**
   * 되돌리기 가능 여부
   */
  get canUndo(): boolean {
    return this.historyIndex > 0;
  }

  /**
   * Enter 키 핸들러
   */
  onPromptEnter(event: KeyboardEvent): void {
    if (event.shiftKey) return;
    event.preventDefault();
    if (this.customPrompt.trim()) {
      this.enhance('custom');
    }
  }

  /**
   * 에러 닫기
   */
  dismissError(): void {
    this.error = null;
  }

  /**
   * 라이트박스 열기 - body에 직접 추가하여 부모 컨테이너 overflow 영향 방지
   */
  openLightbox(): void {
    if (!this.showOverlay && !this.isEnhancing && this.imageUrl) {
      this.showLightbox = true;
      this.createLightboxElement();
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * 라이트박스 닫기
   */
  closeLightbox(): void {
    this.showLightbox = false;
    this.removeLightbox();
    document.body.style.overflow = '';
  }

  /**
   * 라이트박스 DOM 요소 생성 및 body에 추가
   */
  private createLightboxElement(): void {
    if (this.lightboxElement) return;

    // 라이트박스 컨테이너
    const lightbox = this.renderer.createElement('div');
    this.renderer.addClass(lightbox, 'ai-lightbox-portal');
    this.renderer.setStyle(lightbox, 'position', 'fixed');
    this.renderer.setStyle(lightbox, 'top', '0');
    this.renderer.setStyle(lightbox, 'left', '0');
    this.renderer.setStyle(lightbox, 'right', '0');
    this.renderer.setStyle(lightbox, 'bottom', '0');
    this.renderer.setStyle(lightbox, 'background', 'rgba(0, 0, 0, 0.9)');
    this.renderer.setStyle(lightbox, 'z-index', '99999');
    this.renderer.setStyle(lightbox, 'display', 'flex');
    this.renderer.setStyle(lightbox, 'align-items', 'center');
    this.renderer.setStyle(lightbox, 'justify-content', 'center');
    this.renderer.setStyle(lightbox, 'cursor', 'pointer');

    // 이미지 컨테이너
    const content = this.renderer.createElement('div');
    this.renderer.setStyle(content, 'position', 'relative');
    this.renderer.setStyle(content, 'max-width', '90vw');
    this.renderer.setStyle(content, 'max-height', '90vh');
    this.renderer.setStyle(content, 'cursor', 'default');

    // 닫기 버튼
    const closeBtn = this.renderer.createElement('button');
    this.renderer.setProperty(closeBtn, 'innerHTML', '✕');
    this.renderer.setStyle(closeBtn, 'position', 'absolute');
    this.renderer.setStyle(closeBtn, 'top', '-40px');
    this.renderer.setStyle(closeBtn, 'right', '0');
    this.renderer.setStyle(closeBtn, 'background', 'transparent');
    this.renderer.setStyle(closeBtn, 'border', 'none');
    this.renderer.setStyle(closeBtn, 'color', 'white');
    this.renderer.setStyle(closeBtn, 'fontSize', '24px');
    this.renderer.setStyle(closeBtn, 'cursor', 'pointer');
    this.renderer.setStyle(closeBtn, 'padding', '8px');
    const closeBtnListener = this.renderer.listen(closeBtn, 'click', (e: Event) => {
      e.stopPropagation();
      this.closeLightbox();
    });
    this.lightboxListeners.push(closeBtnListener);

    // 이미지
    const img = this.renderer.createElement('img');
    this.renderer.setAttribute(img, 'src', this.imageUrl);
    this.renderer.setStyle(img, 'max-width', '90vw');
    this.renderer.setStyle(img, 'max-height', '90vh');
    this.renderer.setStyle(img, 'object-fit', 'contain');
    this.renderer.setStyle(img, 'border-radius', '8px');

    // 조립
    this.renderer.appendChild(content, closeBtn);
    this.renderer.appendChild(content, img);
    this.renderer.appendChild(lightbox, content);

    // 배경 클릭 시 닫기
    const lightboxListener = this.renderer.listen(lightbox, 'click', () => this.closeLightbox());
    const contentListener = this.renderer.listen(content, 'click', (e: Event) => e.stopPropagation());
    this.lightboxListeners.push(lightboxListener, contentListener);

    // body에 추가
    this.renderer.appendChild(document.body, lightbox);
    this.lightboxElement = lightbox;
  }

  /**
   * 라이트박스 DOM 요소 제거
   */
  private removeLightbox(): void {
    // 이벤트 리스너 정리 (메모리 누수 방지)
    this.lightboxListeners.forEach((unlisten) => unlisten());
    this.lightboxListeners = [];

    if (this.lightboxElement) {
      this.renderer.removeChild(document.body, this.lightboxElement);
      this.lightboxElement = null;
    }
  }

  /**
   * ESC 키로 라이트박스 닫기
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showLightbox) {
      this.closeLightbox();
    }
  }
}
